/**
 * useShareImage Hooks
 *
 * Pre-generates share images when data is available, so they're ready
 * instantly when user clicks share (avoids user gesture expiration).
 *
 * Usage:
 *   // For challenge summary
 *   const { share, isReady } = useShareImage(summaryData);
 *
 *   // For progress share
 *   const { share, isReady } = useProgressShareImage(progressData);
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { shareApi, ShareImageData, ProgressShareData, ChallengeCreationShareData } from '@/api/share';

export interface UseShareImageResult {
  /** Whether the image is pre-generated and ready to share instantly */
  isReady: boolean;
  /** Whether image generation is in progress */
  isGenerating: boolean;
  /** Whether a share operation is in progress */
  isSharing: boolean;
  /** Error from generation or sharing (if any) */
  error: Error | null;
  /**
   * Share the image. Uses pre-generated blob if ready,
   * otherwise generates on-demand (fallback).
   * Returns true if share/download succeeded.
   */
  share: () => Promise<boolean>;
  /** Download the image directly */
  download: () => Promise<boolean>;
}

interface UseShareImageBaseOptions<T> {
  data: T | null;
  generateFn: (data: T) => Promise<Blob>;
  filename: string;
  /** Serialized deps string for triggering regeneration */
  depsKey: string;
}

/**
 * Base hook for share image generation - used by specific hooks below
 */
function useShareImageBase<T>({
  data,
  generateFn,
  filename,
  depsKey,
}: UseShareImageBaseOptions<T>): UseShareImageResult {
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef(true);
  const dataRef = useRef(data);
  dataRef.current = data;

  // Pre-generate image when data changes
  useEffect(() => {
    if (!data) {
      setImageBlob(null);
      return;
    }

    let cancelled = false;
    setIsGenerating(true);
    setError(null);

    generateFn(data)
      .then((blob) => {
        if (!cancelled && isMountedRef.current) {
          setImageBlob(blob);
          setIsGenerating(false);
        }
      })
      .catch((err) => {
        if (!cancelled && isMountedRef.current) {
          setError(err);
          setIsGenerating(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const share = useCallback(async (): Promise<boolean> => {
    if (isSharing) return false;

    setIsSharing(true);
    setError(null);

    try {
      let blobToShare = imageBlob;

      if (!blobToShare) {
        if (!dataRef.current) {
          throw new Error('No share data available');
        }
        blobToShare = await generateFn(dataRef.current);
      }

      const result = await shareApi.shareImage(blobToShare, filename);
      return result;
    } catch (err) {
      setError(err as Error);
      return false;
    } finally {
      if (isMountedRef.current) {
        setIsSharing(false);
      }
    }
  }, [imageBlob, isSharing, generateFn, filename]);

  const download = useCallback(async (): Promise<boolean> => {
    if (isSharing) return false;

    setIsSharing(true);
    setError(null);

    try {
      let blobToDownload = imageBlob;

      if (!blobToDownload) {
        if (!dataRef.current) {
          throw new Error('No share data available');
        }
        blobToDownload = await generateFn(dataRef.current);
      }

      const url = URL.createObjectURL(blobToDownload);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      setError(err as Error);
      return false;
    } finally {
      if (isMountedRef.current) {
        setIsSharing(false);
      }
    }
  }, [imageBlob, isSharing, generateFn, filename]);

  return {
    isReady: imageBlob !== null,
    isGenerating,
    isSharing,
    error,
    share,
    download,
  };
}

/**
 * Hook for challenge summary share images
 */
export function useShareImage(data: ShareImageData | null): UseShareImageResult {
  const depsKey = useMemo(
    () =>
      data
        ? `${data.totalDistance}-${data.totalCalories}-${data.averagePace}-${data.durationDays}-${data.payoutAmount}`
        : '',
    [data?.totalDistance, data?.totalCalories, data?.averagePace, data?.durationDays, data?.payoutAmount]
  );

  return useShareImageBase({
    data,
    generateFn: shareApi.generateShareImage,
    filename: 'dasher-results.png',
    depsKey,
  });
}

/**
 * Hook for progress share images (after activity logging)
 */
export function useProgressShareImage(data: ProgressShareData | null): UseShareImageResult {
  const depsKey = useMemo(
    () =>
      data
        ? `${data.rank}-${data.distance}-${data.calories}-${data.duration}-${data.pace}-${data.completionPercentage}-${data.daysCompleted}`
        : '',
    [data?.rank, data?.distance, data?.calories, data?.duration, data?.pace, data?.completionPercentage, data?.daysCompleted]
  );

  return useShareImageBase({
    data,
    generateFn: shareApi.generateProgressShareImage,
    filename: 'dasher-progress.png',
    depsKey,
  });
}

/**
 * Hook for challenge creation share images
 */
export function useChallengeCreationShareImage(data: ChallengeCreationShareData | null): UseShareImageResult {
  const depsKey = useMemo(
    () =>
      data
        ? `${data.duration}-${data.dailyGoal}-${data.startDate}-${data.participantCount ?? 0}`
        : '',
    [data?.duration, data?.dailyGoal, data?.startDate, data?.participantCount]
  );

  return useShareImageBase({
    data,
    generateFn: shareApi.generateChallengeCreationShareImage,
    filename: 'dasher-challenge.png',
    depsKey,
  });
}
